/**
 * The context chips an empty Benny composer shows (Benny UX pass 4, R4).
 *
 * One query per page shape: the pathname plus whichever record ids the page
 * (or the conversation's anchor) is about. The server answers from the same
 * tool allowlist a chat turn there would get, so a chip can only ask for
 * something Benny can actually do on that page. Cached for a minute — the
 * chips change with the page, not with the clock.
 */

import { useQuery } from '@tanstack/react-query'

import type {
  AssistantCapabilities,
  AssistantCapabilitiesQuery,
} from '@scrambl/shared'

import { assistantApi } from '@/lib/api/assistant.api'
import { useOrganizationStore } from '@/store/organization.store'

const EMPTY: AssistantCapabilities = { chips: [], summary: [] }

export function useBennyCapabilities(
  query: AssistantCapabilitiesQuery,
  options: { enabled?: boolean } = {},
): {
  chips: AssistantCapabilities['chips']
  summary: string[]
  isLoading: boolean
} {
  const orgId = useOrganizationStore(state => state.currentOrganization?.id)
  const result = useQuery({
    queryKey: [
      'benny-capabilities',
      orgId,
      query.pathname ?? '',
      query.taskId ?? '',
      query.projectId ?? '',
      query.entityId ?? '',
    ],
    queryFn: () => assistantApi.getCapabilities(query),
    enabled: Boolean(orgId) && options.enabled !== false,
    staleTime: 60_000,
  })
  const data = result.data ?? EMPTY
  return { chips: data.chips, summary: data.summary, isLoading: result.isLoading }
}
